import conchoFloralRaw from "../icons/hardware-concho-floral.svg?raw";
import conchoRoundRaw from "../icons/hardware-concho-round.svg?raw";
import conchoStarRaw from "../icons/hardware-concho-star.svg?raw";
import eyeletRaw from "../icons/hardware-eyelet.svg?raw";
import magneticSnapRaw from "../icons/hardware-magnetic-snap.svg?raw";
import rivetRaw from "../icons/hardware-rivet.svg?raw";
import snapRaw from "../icons/hardware-snap.svg?raw";
import { stripIconSvg } from "../icons/stripIconSvg";
import type { HardwareSide, HardwareType } from "../model/Project";

/** Build the SVG path data for a hardware item drawn at the origin (0,0)
 *  with the given visible cap diameter (project mm). The returned string
 *  can be fed straight into a paper.js `CompoundPath({ pathData })`.
 *
 *  Each path is intentionally one self-contained string (with sub-paths
 *  separated by `M` commands) so the caller can pick a single fill
 *  colour and a single rim stroke and have the cap render correctly.
 *
 *  `side` only affects two-part hardware (snap, magnetic snap):
 *    - female (top / socket) — concentric rings reading as an indented well.
 *    - male   (bottom / stud) — solid cap with a small raised dome in the
 *                              centre to suggest the protruding stud. */
export function hardwarePathData(
  type: HardwareType,
  capMm: number,
  side: HardwareSide = "female",
): string {
  const r = capMm / 2;
  switch (type) {
    case "rivet":
      // Solid round cap.
      return circle(0, 0, r);
    case "snap":
      if (side === "male") {
        // Cap + small raised stud in the centre.
        return circle(0, 0, r) + " " + circle(0, 0, r * 0.25);
      }
      // Female: outer cap + inner pressed-down well (concentric rings).
      return circle(0, 0, r) + " " + circle(0, 0, r * 0.45, /*reverse*/ true);
    case "magnetic-snap":
      if (side === "male") {
        // Cap + slightly larger raised stud than a regular snap (magnet
        // sits proud) — no inner well.
        return circle(0, 0, r) + " " + circle(0, 0, r * 0.42);
      }
      // Female: cap + indented ring + tiny centre dot suggesting the magnet.
      return (
        circle(0, 0, r) +
        " " +
        circle(0, 0, r * 0.7, true) +
        " " +
        circle(0, 0, r * 0.18)
      );
    case "eyelet":
      // Donut: outer flange minus inner barrel hole.
      return circle(0, 0, r) + " " + circle(0, 0, r * 0.45, true);
    case "concho-round":
      // Plain round flat concho — same silhouette as a rivet but read as
      // its own kind by virtue of the much larger size.
      return circle(0, 0, r);
    case "concho-star":
      return star(0, 0, r, r * 0.45, 5);
    case "concho-floral":
      return rosette(0, 0, r, 6);
  }
}

/** True for hardware kinds that have a meaningful male / female pair
 *  (snap, magnetic snap). One-piece kinds return false — the panel uses
 *  this to decide whether to show the male / female toggle. */
export function hasSide(type: HardwareType): boolean {
  return type === "snap" || type === "magnetic-snap";
}

/** SVG path commands for a circle. Pass `reverse=true` to wind it the
 *  other way so it cuts a hole when used as a sub-path of a compound
 *  (paper.js + SVG fill-rule both honour the winding flip). */
function circle(cx: number, cy: number, r: number, reverse = false): string {
  if (reverse) {
    return (
      `M ${cx + r} ${cy} ` +
      `A ${r} ${r} 0 1 0 ${cx - r} ${cy} ` +
      `A ${r} ${r} 0 1 0 ${cx + r} ${cy} Z`
    );
  }
  return (
    `M ${cx + r} ${cy} ` +
    `A ${r} ${r} 0 1 1 ${cx - r} ${cy} ` +
    `A ${r} ${r} 0 1 1 ${cx + r} ${cy} Z`
  );
}

/** Regular n-pointed star alternating between `outerR` and `innerR`. */
function star(
  cx: number,
  cy: number,
  outerR: number,
  innerR: number,
  points: number,
): string {
  const segs: string[] = [];
  // Start at the topmost outer point so the star reads upright.
  for (let i = 0; i < points * 2; i++) {
    const radius = i % 2 === 0 ? outerR : innerR;
    const angle = -Math.PI / 2 + (i * Math.PI) / points;
    const x = cx + radius * Math.cos(angle);
    const y = cy + radius * Math.sin(angle);
    segs.push(`${i === 0 ? "M" : "L"} ${x} ${y}`);
  }
  segs.push("Z");
  return segs.join(" ");
}

/** Floral rosette: `petals` overlapping circles around the centre, plus
 *  a centre cap for cohesion. Yields a "flower" silhouette familiar from
 *  Western-style conchos. */
function rosette(
  cx: number,
  cy: number,
  outerR: number,
  petals: number,
): string {
  const petalR = outerR * 0.45;
  const orbitR = outerR - petalR;
  const parts: string[] = [];
  for (let i = 0; i < petals; i++) {
    const angle = (i * 2 * Math.PI) / petals - Math.PI / 2;
    const px = cx + orbitR * Math.cos(angle);
    const py = cy + orbitR * Math.sin(angle);
    parts.push(circle(px, py, petalR));
  }
  parts.push(circle(cx, cy, outerR * 0.32));
  return parts.join(" ");
}

/** Previews for the type gallery tiles in the properties panel, read from
 *  `src/icons/hardware-*.svg` through the shared {@link stripIconSvg} (#216).
 *  24×24 with `currentColor` so they pick up the panel's text colour and the
 *  accent on selection, matching the cutout / shape galleries' convention. */
export const HARDWARE_TYPE_ICONS: Record<HardwareType, string> = {
  rivet: stripIconSvg(rivetRaw),
  snap: stripIconSvg(snapRaw),
  "magnetic-snap": stripIconSvg(magneticSnapRaw),
  eyelet: stripIconSvg(eyeletRaw),
  "concho-round": stripIconSvg(conchoRoundRaw),
  "concho-star": stripIconSvg(conchoStarRaw),
  "concho-floral": stripIconSvg(conchoFloralRaw),
};

/** Lighten / darken a hex colour by `amt` ∈ [-1,1]. Used for the rim shade
 *  vs face shade so the cap reads with a hint of depth at any colour. */
export function shadeHex(hex: string, amt: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1]!, 16);
  let r = (n >> 16) & 0xff;
  let g = (n >> 8) & 0xff;
  let b = n & 0xff;
  if (amt >= 0) {
    r = Math.round(r + (255 - r) * amt);
    g = Math.round(g + (255 - g) * amt);
    b = Math.round(b + (255 - b) * amt);
  } else {
    r = Math.round(r * (1 + amt));
    g = Math.round(g * (1 + amt));
    b = Math.round(b * (1 + amt));
  }
  return (
    "#" +
    [r, g, b]
      .map((v) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, "0"))
      .join("")
  );
}
